import { extractCompanySuffix } from './company-name';

/** Map PRH / registry legal-form phrases to a short label for the avatar ribbon. */
const REGISTRY_FORM_ABBREVIATIONS: { pattern: RegExp; short: string }[] = [
  { pattern: /julkinen\s+osakeyhtiö|public\s+limited\s+company/i, short: 'Oyj' },
  { pattern: /osakeyhtiö|private\s+limited|limited\s+company/i, short: 'Oy' },
  { pattern: /aktiebolag|aktiebolaget/i, short: 'Ab' },
  { pattern: /kommandiittiyhtiö|kommanditbolag/i, short: 'Ky' },
  { pattern: /tmi\.?|toiminimi|enskild\s+firma/i, short: 'Tmi' },
  { pattern: /handelsbolag/i, short: 'HB' },
  { pattern: /kommanditbolag/i, short: 'KB' },
  { pattern: /osk\.?|osuuskunta/i, short: 'Osk' },
  { pattern: /yhdistys|förening|association/i, short: 'Ry' },
  { pattern: /säätiö|stiftelse|foundation/i, short: 'Sr' },
  { pattern: /\bllc\b|l\.l\.c/i, short: 'LLC' },
  { pattern: /\bltd\.?\b|limited\b/i, short: 'Ltd' },
  { pattern: /\binc\.?\b|incorporated/i, short: 'Inc' },
  { pattern: /\bcorp\.?\b|corporation/i, short: 'Corp' },
  { pattern: /\bplc\.?\b/i, short: 'Plc' },
];

/**
 * Company name for the script line: strips legal suffix (Oy, Ab, …) so the form row can show it.
 */
export function getCompanyLogoScriptDisplayName(sourceName: string): string {
  const trimmed = (sourceName || '').trim();
  if (!trimmed) return '';
  const { mainName } = extractCompanySuffix(trimmed);
  return (mainName || trimmed).trim() || trimmed;
}

/** Hard cap so DOM/SSR stay bounded; normal names wrap in the tile via CSS. */
const MAX_SCRIPT_DISPLAY_CHARS = 96;

/**
 * Only truncates extremely long names (layout uses word wrap + line clamp in the component).
 * When truncating, prefers ending at a word boundary.
 */
export function truncateCompanyScriptNameForBox(
  name: string,
  _sizePx: number,
  _formRow: boolean
): string {
  const n = (name || '').trim();
  if (!n) return '';
  if (n.length <= MAX_SCRIPT_DISPLAY_CHARS) return n;

  const budget = MAX_SCRIPT_DISPLAY_CHARS;
  const slice = n.slice(0, budget - 1);
  const lastSpace = slice.lastIndexOf(' ');
  if (lastSpace > budget * 0.5) return `${slice.slice(0, lastSpace)}…`;
  return `${slice}…`;
}

function shortenParsedSuffix(suffix: string): string {
  const s = suffix.trim().replace(/\.$/, '');
  if (!s) return '';
  if (s.length <= 6) return s;
  const lower = s.toLowerCase();
  if (/osakeyhtiö/.test(lower)) return 'Oy';
  if (/aktiebolag/.test(lower)) return 'Ab';
  if (/yhtiö/.test(lower)) return 'Oy';
  return s.slice(0, 8);
}

/**
 * Short legal-form label: registry description when useful, else suffix parsed from the name.
 */
export function getCompanyLogoFormShortLabel(
  sourceName: string,
  registryCompanyFormDescription?: string | null,
  explicitShortLabel?: string | null
): string {
  if (explicitShortLabel?.trim()) return explicitShortLabel.trim();

  const desc = registryCompanyFormDescription?.trim();
  if (desc) {
    for (const { pattern, short } of REGISTRY_FORM_ABBREVIATIONS) {
      if (pattern.test(desc)) return short;
    }
    if (desc.length <= 10) return desc;
    const first = desc.split(/\s+/)[0];
    if (first && first.length <= 12) return first;
    return `${desc.slice(0, 9)}…`;
  }

  const { suffix } = extractCompanySuffix(sourceName.trim());
  if (!suffix) return '';
  return shortenParsedSuffix(suffix);
}
